import type { TournamentDivision, TournamentFormatConfig, TournamentMatch } from "./api";
import { groupNameWithDivision } from "./groupLabels";

export interface RoundRobinPhaseConfigLike {
  schema_version?: number;
  format_mode?: string;
  round_count?: number;
  group_stage?: {
    schedule_type?: string;
    matches_per_player?: number | null;
    group_count?: number;
    players_per_group?: number;
  };
  qualification?: {
    rules?: Array<Record<string, unknown>>;
    seed_method?: string;
  };
  playoff?: {
    bracket_policy?: string;
  };
}

export function activeRoundRobinPhaseConfig(
  formatConfig: TournamentFormatConfig | null | undefined,
  activeDivision: TournamentDivision | null | undefined,
): RoundRobinPhaseConfigLike | null {
  const raw = activeDivision?.format_config_json ?? formatConfig?.config_json ?? null;
  if (!raw || typeof raw !== "object") return null;
  return raw as RoundRobinPhaseConfigLike;
}

export function isRoundRobinPlayoffPhase(config: RoundRobinPhaseConfigLike | null | undefined): boolean {
  return config?.schema_version === 2 && ["round_robin_playoffs", "ladder_playoffs"].includes(config?.format_mode ?? "");
}

export function groupScheduleTypeLabel(config: RoundRobinPhaseConfigLike | null | undefined): string {
  const type = config?.group_stage?.schedule_type ?? "full_round_robin";
  if (type === "limited_schedule") return "Calendario limitado";
  if (type === "manual_schedule") return "Calendario manual";
  return "Todos contra todos";
}

export function bracketPolicyLabel(policy: string | null | undefined): string {
  if (policy === "auto_byes") return "bracket automático con BYEs";
  if (policy === "manual") return "bracket manual";
  return "bracket automático con preliminar";
}

export function qualificationRulesSummary(config: RoundRobinPhaseConfigLike | null | undefined): string {
  const rules = config?.qualification?.rules ?? [];
  const pieces = rules.map((rule) => {
    if (rule.type === "top_n_per_group" || rule.type === "top_n_per_category") {
      const from = Number(rule.positions_from ?? 1);
      const to = Number(rule.positions_to ?? from);
      const scope = rule.type === "top_n_per_category" || config?.format_mode === "ladder_playoffs" ? "categoría" : "grupo";
      return from === to ? `${from}° de cada ${scope}` : `${from}°–${to}° de cada ${scope}`;
    }
    if (rule.type === "best_n_by_group_position") {
      return `${Number(rule.count ?? 0)} mejor(es) ${Number(rule.group_position ?? 0)}°`;
    }
    if (rule.type === "best_n_overall") {
      return `${Number(rule.count ?? 0)} mejor(es) generales`;
    }
    return String(rule.type ?? "regla");
  });
  return pieces.length ? pieces.join(" + ") : "reglas de clasificación pendientes";
}

export function roundRobinPhaseSummary(config: RoundRobinPhaseConfigLike | null | undefined): string {
  if (!isRoundRobinPlayoffPhase(config)) return "";
  if (config?.format_mode === "ladder_playoffs") {
    return `Escalera por categoría → clasifican ${qualificationRulesSummary(config)} → ${bracketPolicyLabel(config?.playoff?.bracket_policy)}.`;
  }
  return `${groupScheduleTypeLabel(config)} → clasifican ${qualificationRulesSummary(config)} → ${bracketPolicyLabel(config?.playoff?.bracket_policy)}.`;
}

export function isGeneratedRoundRobinPlayoffMatch(
  match: TournamentMatch,
  config: RoundRobinPhaseConfigLike | null | undefined,
): boolean {
  if (match.group_id !== null) return false;
  if (typeof match.notes === "string" && /\bPlayoff v2\b/.test(match.notes)) return true;
  if (!isRoundRobinPlayoffPhase(config)) return false;
  return match.group_id === null && /playoff/i.test(match.notes ?? "");
}

/** Lo mínimo de un grupo para nombrarlo (TournamentGroup lo cumple). */
export interface MatchGroupNameSource {
  id: number;
  name: string;
  is_active?: boolean;
}

/**
 * Etiqueta «Grupo» de un partido: columna de los PDFs de calendario y
 * resultados, CSV y renglones del rol de juegos. Pedido de Eduardo
 * 2026-10-01 (torneo 59, grupos + playoffs): antes decía «Fase de grupos» en
 * TODOS los renglones y el jugador no sabía en qué grupo estaba.
 * - Con grupo → su nombre real («Grupo 1»): primero el de la lista de grupos
 *   cargada si está activo; si la lista no está cargada (vista del jugador) o
 *   el grupo está inactivo, el `group_name` que ya trae el partido; al final
 *   «Grupo #id».
 * - Sin grupo y de playoffs (`isPlayoffMatch`, lo decide la página) →
 *   «Playoffs».
 * - Sin grupo → «Sin grupo».
 * - `repeatedNames` (Eduardo 2026-10-01: «Grupo 1» puede existir en dos
 *   categorías): si el nombre está entre los repetidos de lo que se muestra,
 *   va con la categoría del partido → «Grupo 1 (Sub-10)».
 */
export function matchGroupLabel(
  match: Pick<TournamentMatch, "group_id" | "group_name"> & { division_name?: string | null },
  groups: readonly MatchGroupNameSource[],
  isPlayoffMatch: boolean,
  repeatedNames?: ReadonlySet<string>,
): string {
  if (match.group_id === null) return isPlayoffMatch ? "Playoffs" : "Sin grupo";
  const group = groups.find((candidate) => candidate.id === match.group_id);
  const name =
    group && group.is_active !== false
      ? group.name
      : match.group_name?.trim() || group?.name || `Grupo #${match.group_id}`;
  return repeatedNames ? groupNameWithDivision(name, match.division_name, repeatedNames) : name;
}

// Knockout stage name from the number of matches in that round (single
// elimination: 16 matches = Dieciseisavos, 8 = Octavos, … 1 = Final).
const KNOCKOUT_STAGE_BY_MATCH_COUNT: Record<number, string> = {
  1: "Final",
  2: "Semifinal",
  4: "Cuartos de final",
  8: "Octavos de final",
  16: "Dieciseisavos de final",
};

export function knockoutStageName(matchCount: number): string {
  return KNOCKOUT_STAGE_BY_MATCH_COUNT[matchCount] ?? `Ronda de ${matchCount * 2}`;
}

/**
 * Single source of truth for elimination-bracket round names in Spanish. The
 * backend stores English keys ("Round of 16", "Semifinals"); this is the only
 * place that maps them for display, shared by the schedule and rounds views.
 */
export function eliminationRoundDisplayLabel(label: string | null | undefined): string {
  switch ((label ?? "").trim()) {
    case "Preliminar":
      return "Preliminar";
    case "Round of 256":
      return "Ronda de 256";
    case "Round of 128":
      return "Ronda de 128";
    case "Round of 64":
      return "Ronda de 64";
    case "Round of 32":
      return "Dieciseisavos de final";
    case "Round of 16":
      return "Octavos de final";
    case "Quarterfinals":
      return "Cuartos de final";
    case "Semifinals":
      return "Semifinal";
    case "Final":
      return "Final";
    case "3er Lugar":
      return "3er lugar";
    default:
      return (label ?? "").trim() || "Sin ronda";
  }
}

/** "Jornada 7" / "Semana 7" / "J7" → 7; null si no es una ronda numerada. */
export function parsePlayoffJornadaNumber(label: string | null | undefined): number | null {
  const found = (label ?? "").trim().match(/^(?:semana|jornada|matchups?|j)\s*#?\s*(\d+)/i);
  if (!found) return null;
  const value = Number(found[1]);
  return Number.isFinite(value) && value > 0 ? value : null;
}

/** La preliminar de playoffs v2 persiste "Preliminar" en las notes de sus
 * partidos ("Playoff v2 · Preliminar · …"); es la única señal estable porque
 * el round_label sigue siendo "Jornada N". Espejo de `_PRELIM_NOTES_RE`. */
function isPreliminaryPlayoffRound(matches: TournamentMatch[]): boolean {
  return matches.some((match) => /preliminar/i.test(match.notes ?? ""));
}

// ---------------------------------------------------------------------------
// PREDICADO COMPARTIDO de partidos de playoffs — única fuente de verdad para
// la derivación de etapas (playoffStageLabelsByRound) y para el bracket del
// jugador (bracketFromMatches). Es el espejo EXACTO de
// `is_playoff_round_match` + el filtro de cancelados de
// backend/app/modules/matches/playoff_labels.py, para que ambos lados
// produzcan la MISMA etiqueta (o el MISMO fallback crudo).
// ---------------------------------------------------------------------------

/** Marcador persistido por las dos generaciones de playoffs. Espejo de
 * `_PLAYOFF_NOTES_RE`: v2 ("Playoff v2 · …") y v1 legado
 * ("Playoff semifinal: …" / "Playoff ronda N: …"). */
const PLAYOFF_NOTES_MARKER = /\bPlayoff v2\b|^Playoff (?:semifinal|ronda)\b/;

export function hasPlayoffNotesMarker(notes: string | null | undefined): boolean {
  return PLAYOFF_NOTES_MARKER.test(notes ?? "");
}

/** Espejo de `MATCH_STATUS_CANCELLED`. Un partido cancelado NUNCA cuenta para
 * la estructura del cuadro: una Final cancelada + regenerada dejaría DOS filas
 * en la jornada final, el árbol parecería incompleto y la Final activa se
 * mostraría como "Semifinal". */
export function isCancelledPlayoffMatch(match: Pick<TournamentMatch, "status">): boolean {
  return (match.status ?? "").trim().toLowerCase() === "cancelled";
}

/** ¿El partido cuenta para la ESTRUCTURA del cuadro de playoffs? Sin grupo +
 * round_label "Jornada N" + marcador de playoffs en notes (v1 o v2), y no
 * cancelado. Idéntico al criterio del backend. */
export function isStructuralPlayoffMatch(match: TournamentMatch): boolean {
  return (
    !isCancelledPlayoffMatch(match)
    && match.group_id === null
    && parsePlayoffJornadaNumber(match.round_label) !== null
    && hasPlayoffNotesMarker(match.notes)
  );
}

/** Posición desde la Final (0 = última ronda) → etapa. Espejo de
 * `_STAGE_KEY_FROM_FINAL`: más allá de la Ronda de 256 no hay etiqueta. */
const STAGE_FROM_FINAL: readonly string[] = [
  "Final",
  "Semifinal",
  "Cuartos de final",
  "Octavos de final",
  "Dieciseisavos de final",
  "Ronda de 64",
  "Ronda de 128",
  "Ronda de 256",
];

/** Conteo de partidos → etapa, SOLO para árboles incompletos/legados. Espejo
 * de `_STAGE_KEY_BY_MATCH_COUNT`: únicamente potencias de 2. Cualquier otro
 * conteo se queda SIN etiqueta y el consumidor cae a la etiqueta CRUDA
 * ("Jornada N") — nunca inventamos una "Ronda de 6". */
const STAGE_BY_MATCH_COUNT: Record<number, string> = {
  1: "Final",
  2: "Semifinal",
  4: "Cuartos de final",
  8: "Octavos de final",
  16: "Dieciseisavos de final",
  32: "Ronda de 64",
  64: "Ronda de 128",
  128: "Ronda de 256",
};

/** Etapa por posición desde la Final; null fuera de la tabla. */
function stageFromFinalPosition(fromFinal: number): string | null {
  return fromFinal >= 0 && fromFinal < STAGE_FROM_FINAL.length
    ? STAGE_FROM_FINAL[fromFinal]
    : null;
}

/** Etapa por conteo de partidos; null si el conteo no es potencia de 2. */
function stageFromMatchCount(count: number): string | null {
  return Object.prototype.hasOwnProperty.call(STAGE_BY_MATCH_COUNT, count)
    ? STAGE_BY_MATCH_COUNT[count]
    : null;
}

/**
 * Etiquetas canónicas de tenis para una secuencia ORDENADA (primera → última)
 * de rondas de playoffs. Contrato de derivación (decisión de producto,
 * E2E Copa Crítica hallazgo 8):
 * - La ronda preliminar (detectada por notes) es "Preliminar".
 * - Si la última ronda tiene EXACTAMENTE 1 partido (la materialización
 *   siempre completa el esqueleto hasta la final), las demás se nombran
 *   hacia atrás: Final ← Semifinal ← Cuartos de final ← Octavos de final ←
 *   Dieciseisavos de final. Esto es robusto ante BYEs/pases directos que
 *   reducen los partidos persistidos de una ronda.
 * - Si el árbol no termina en 1 partido (datos legados incompletos), cada
 *   ronda se nombra por su propio conteo y SOLO si es potencia de 2.
 * - Los partidos CANCELADOS se excluyen ANTES de contar: una Final cancelada
 *   y regenerada deja dos filas en la jornada final y el árbol parecería
 *   incompleto (mismo criterio que el backend).
 *
 * Devuelve `null` en las rondas sin etiqueta confiable: el consumidor debe
 * caer a la etiqueta CRUDA ("Jornada N"), igual que el backend.
 */
export function playoffStageSequenceForRounds(rounds: TournamentMatch[][]): (string | null)[] {
  const labels = new Array<string | null>(rounds.length).fill(null);
  const activeCounts = new Array<number>(rounds.length).fill(0);
  const stageIndexes: number[] = [];
  rounds.forEach((roundMatches, index) => {
    const active = roundMatches.filter((match) => !isCancelledPlayoffMatch(match));
    activeCounts[index] = active.length;
    // Una ronda que solo tiene cancelados no existe para el backend: no puede
    // desplazar el conteo de etapas hacia atrás.
    if (active.length === 0) return;
    if (isPreliminaryPlayoffRound(active)) {
      labels[index] = "Preliminar";
    } else {
      stageIndexes.push(index);
    }
  });
  if (stageIndexes.length === 0) return labels;
  const treeComplete = activeCounts[stageIndexes[stageIndexes.length - 1]] === 1;
  stageIndexes.forEach((roundIndex, position) => {
    labels[roundIndex] = treeComplete
      ? stageFromFinalPosition(stageIndexes.length - 1 - position)
      : stageFromMatchCount(activeCounts[roundIndex]);
  });
  return labels;
}

export interface PlayoffStageEntry {
  divisionId: number | null;
  roundLabel: string;
  stage: string;
}

/**
 * Etapa canónica por (división, round_label). ESPEJO EXACTO de
 * `playoff_stage_display_for_matches` del backend: mismo predicado, mismo
 * filtro de cancelados, mismo scope por división y las mismas rondas omitidas
 * cuando no hay etiqueta confiable. Es el punto de comparación de las pruebas
 * de paridad frontend/backend.
 *
 * Solo devuelve las rondas CON etiqueta confiable; el resto conserva su
 * "Jornada N" cruda.
 */
export function playoffStageEntriesForMatches(matches: TournamentMatch[]): PlayoffStageEntry[] {
  type Bucket = { labels: Set<string>; matches: TournamentMatch[] };
  const byDivision = new Map<string, { divisionId: number | null; rounds: Map<number, Bucket> }>();
  for (const match of matches) {
    if (!isStructuralPlayoffMatch(match)) continue;
    const label = (match.round_label ?? "").trim();
    const jornada = parsePlayoffJornadaNumber(label);
    if (!label || jornada === null) continue;
    const divisionId = match.division_id ?? null;
    const divisionKey = divisionId === null ? "none" : String(divisionId);
    const scope = byDivision.get(divisionKey) ?? { divisionId, rounds: new Map<number, Bucket>() };
    const bucket = scope.rounds.get(jornada) ?? { labels: new Set<string>(), matches: [] as TournamentMatch[] };
    // Aliases del mismo número ("Semana 4" / "Jornada 4"): todos se acumulan
    // en el MISMO bucket estructural (no cuentan como rondas extra), igual
    // que el backend.
    bucket.labels.add(label);
    bucket.matches.push(match);
    scope.rounds.set(jornada, bucket);
    byDivision.set(divisionKey, scope);
  }
  const entries: PlayoffStageEntry[] = [];
  for (const scope of byDivision.values()) {
    const ordered = [...scope.rounds.entries()].sort(([a], [b]) => a - b).map(([, bucket]) => bucket);
    const stages = playoffStageSequenceForRounds(ordered.map((bucket) => bucket.matches));
    ordered.forEach((bucket, index) => {
      const stage = stages[index];
      // Sin etiqueta confiable (conteo que no es potencia de 2, ronda solo con
      // cancelados): se deja "Jornada N" cruda, igual que el backend.
      if (stage === null) return;
      // La etapa derivada se asigna a CADA alias crudo del bucket, de forma
      // determinista (orden lexicográfico, no orden de llegada de las filas).
      for (const bucketLabel of [...bucket.labels].sort()) {
        entries.push({ divisionId: scope.divisionId, roundLabel: bucketLabel, stage });
      }
    });
  }
  return entries;
}

/**
 * Map each round-robin/escalera playoff round_label to its canonical Spanish
 * knockout stage name. Presentation only — the stored round_label
 * ("Jornada N") stays as the internal key everywhere (orden, avance de
 * ganadores, precedencia de calendario: NO tocar ese contrato).
 *
 * Varias categorías (divisiones) comparten los números de "Jornada N": la
 * derivación corre POR división y, si la misma etiqueta resuelve a etapas
 * distintas en divisiones distintas, se omite del mapa (mejor "Jornada N"
 * cruda que una etapa equivocada).
 *
 * No recibe config: el criterio es el del backend (`isStructuralPlayoffMatch`),
 * que cubre v2 y v1 legado por igual y no depende de la configuración cargada.
 */
export function playoffStageLabelsByRound(
  matches: TournamentMatch[],
): Record<string, string> {
  const out: Record<string, string> = {};
  const conflicting = new Set<string>();
  for (const entry of playoffStageEntriesForMatches(matches)) {
    if (entry.roundLabel in out && out[entry.roundLabel] !== entry.stage) {
      conflicting.add(entry.roundLabel);
      continue;
    }
    out[entry.roundLabel] = entry.stage;
  }
  for (const label of conflicting) delete out[label];
  return out;
}


export interface PlayoffVisualRound {
  roundNumber: number;
  label: string;
  matches: TournamentMatch[];
}

/** Agrupa los partidos de PLAYOFFS en las rondas del cuadro visual.
 *
 * La pertenencia la decide SOLO el predicado autoritativo
 * (`isStructuralPlayoffMatch` vía el callback `isPlayoff`, que reconoce el
 * marcador en notes y las etiquetas canónicas legadas). El número de ronda
 * únicamente AGRUPA y ORDENA.
 *
 * Regresión que cierra (caso t54 división 66, Eduardo 2026-08-04): antes se
 * descartaba todo partido cuyo número de ronda no superara el `round_count`
 * configurado, así que con 3 jornadas jugadas y el Formato en 4+, la
 * Semifinal ("Jornada 4") y la Final ("Jornada 5") desaparecían del cuadro —
 * la página solo mostraba el PREVIEW de simulación, sin resultados ni
 * campeón, mientras el bloqueo de seeds sí los reconocía. Dos criterios
 * distintos para la misma pregunta; ahora manda el predicado.
 */
export function groupPlayoffVisualRounds(
  matches: TournamentMatch[],
  options: {
    isPlayoff: (match: TournamentMatch) => boolean;
    roundNumberFor: (match: TournamentMatch) => number | null;
  },
): PlayoffVisualRound[] {
  const byRound = new Map<number, TournamentMatch[]>();
  for (const match of matches) {
    if (!options.isPlayoff(match)) continue;
    const roundNumber = options.roundNumberFor(match);
    if (roundNumber === null) continue;
    const list = byRound.get(roundNumber) ?? [];
    list.push(match);
    byRound.set(roundNumber, list);
  }
  const sortedRounds = [...byRound.entries()].sort(([a], [b]) => a - b);
  // Etiquetas canónicas derivadas de la ESTRUCTURA (Final hacia atrás), misma
  // fuente que el rol de juegos y el bracket del jugador.
  const stageLabels = playoffStageSequenceForRounds(
    sortedRounds.map(([, roundMatches]) => roundMatches),
  );
  return sortedRounds.map(([roundNumber, roundMatches], index) => {
    const sortedMatches = [...roundMatches].sort((a, b) => {
      const aNumber = a.match_number ?? 1_000_000;
      const bNumber = b.match_number ?? 1_000_000;
      return aNumber - bNumber || a.id - b.id;
    });
    return {
      roundNumber,
      // Sin etiqueta confiable (conteo que no es potencia de 2) se conserva
      // la etiqueta CRUDA de la BD, igual que el backend.
      label: stageLabels[index] ?? (sortedMatches[0]?.round_label || `Jornada ${roundNumber}`),
      matches: sortedMatches,
    };
  });
}
